// Imports
import React, { useState } from 'react';
import styled from 'styled-components/macro';
import Input from '../../../../foundation/front-end/components/forms/fields/input';
import APIForm, { APIFormOptions } from '../form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faPen as faPenRegular, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { faPen as faPenSolid } from '@fortawesome/pro-solid-svg-icons';
import helperFunctions from '../../../../foundation/front-end/utils/helper-functions';
import useUpdateOnlyEffect from '../../../../foundation/front-end/utils/use-update-only-effect';
import { z } from 'zod';
import Button from '../../../../foundation/front-end/components/buttons/standard';
import Tippy from '@tippyjs/react';
import tippyProps from '../../../../foundation/front-end/utils/tippy-props';


// Styled components
const Container = styled.td`
	form {
		display: inline-flex !important;
		gap: 0.75rem;
		
		div[class^='field-container'] {
			margin-bottom: 0;
		}
		
		> div:last-child {
			flex-wrap: nowrap;
		}
		
		input {
			min-width: 12.5rem;
		}
	}
`;

const Content = styled.span`
	cursor: pointer;
	display: flex;
	align-items: center;
	column-gap: 0.6rem;
	
	svg.fa-pen {
		opacity: 0;
		transition: opacity 0.15s ease;
		color: #aaa;
	}
	
	&:hover,
	&:focus {
		svg.fa-pen {
			opacity: 1;
		}
	}
`;


// Define the accepted props
interface Props {
	action: string;
	uri: string;
	initialValue?: string;
	name: string;
	maxLength?: number;
	outerProps?: Omit<React.DetailedHTMLProps<React.TdHTMLAttributes<HTMLTableCellElement>, HTMLTableCellElement>, 'ref'>;
	innerProps?: Omit<React.DetailedHTMLProps<React.HTMLAttributes<HTMLSpanElement>, HTMLSpanElement>, 'ref'>;
	prefix?: React.ReactElement;
	suffix?: React.ReactElement;
	beforeContent?: React.ReactElement;
	afterContent?: React.ReactElement;
	onCompletion?: React.ComponentProps<typeof APIForm>['onCompletion'];
	fallbackText?: React.ReactElement;
	required?: boolean;
	storybookEditing?: true;
}


// Functional component
function RenamableTableCell<Parameters = undefined, Files = undefined, Response = undefined>({
	action,
	uri,
	initialValue,
	name,
	maxLength,
	outerProps,
	innerProps,
	prefix,
	suffix,
	beforeContent,
	afterContent,
	onCompletion,
	fallbackText,
	required,
	storybookEditing,
}: Props): React.ReactElement {
	// Use state
	const [cellContent, setCellContent] = useState<string>(initialValue ?? '');
	const [editing, setEditing] = useState<boolean>(Boolean(storybookEditing));
	
	
	// Handle clicks on the table cell text
	const startEditing = () => {
		if (editing) {
			return;
		}
		
		setEditing(true);
	};
	
	
	// Handle changes to the initial value
	useUpdateOnlyEffect(() => {
		setCellContent(initialValue ?? '');
	}, [initialValue]);
	
	
	// Handle unexpected usage
	if (required === false && !fallbackText) {
		throw new Error('A fallback must be provided if the field is optional');
	}
	
	
	// Handle completion of the edit form
	const handleCompletion = async (json: SuccessfulAPIResponse<Response>, parameters: Parameters) => {
		// Parse and set the content of the cell
		const parametersSchema = z.object({
			[name]: required ? z.string() : z.string().optional(),
		});
		
		const parsedParameters = parametersSchema.parse(parameters);
		
		setCellContent(parsedParameters[name] ?? '');
		
		
		// Call the provided completion handler
		const response = onCompletion?.(json, parameters);
		
		
		// If it's a promise, await it so the form appears to load until it's complete
		if (response instanceof Promise) {
			await response;
		}
		
		
		// Hide the form
		setEditing(false);
	};
	
	
	// Handle canceling the edit form
	const handleCancel = () => {
		setEditing(false);
	};
	
	
	// Create options for API form component
	const formOptions: APIFormOptions<Parameters, Files, Response> = {
		action: action,
		uri: uri,
		submitButton: {
			text: <FontAwesomeIcon icon={faCheck} />,
			tooltip: 'Save',
			ariaLabel: 'Save edit',
		},
		extraControls: [
			<Tippy key='cancel' content='Cancel' {...tippyProps}>
				<Button type='button' color='plain' aria-label='Cancel editing' onClick={handleCancel} border>
					<FontAwesomeIcon icon={faXmark} />
				</Button>
			</Tippy>,
		],
		recoverState: false,
		unsavedChangesWarning: {
			disabled: true,
		},
		appearsOnBackground: false,
	};
	
	
	// Return JSX
	return (
		<Container {...outerProps}>
			{editing ? (
				<APIForm options={formOptions} onCompletion={handleCompletion}>
					<Input
						type='text'
						name={name}
						initialValue={initialValue}
						placeholder={initialValue}
						icon={faPenSolid}
						maxLength={maxLength}
						required={required ?? true}
						autoFocus
						onFocus={(event) => event.target.select()}
					/>
				</APIForm>
			) : (
				<React.Fragment>
					{beforeContent}
					
					<Content {...helperFunctions.buttonize(startEditing)} {...innerProps}>
						<span>
							{prefix}
							{cellContent || fallbackText}
							{suffix}
						</span>
						
						<FontAwesomeIcon icon={faPenRegular} />
					</Content>
					
					{afterContent}
				</React.Fragment>
			)}
		</Container>
	);
}

export default RenamableTableCell;
